'use client';
import classNames from 'classnames';
import { FC, startTransition, useActionState, useCallback, useContext, useEffect, useRef } from 'react';

import { AlertIconXs } from '@lichtblick/icons/svg/alert/alert-xs';
import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { TextCTA } from '@lichtblick/photon/atoms/text-cta/text-cta';
import { FieldFooter } from '@lichtblick/photon/molecules/field-footer/field-footer';
import { TextfieldProps } from '@lichtblick/photon/molecules/textfield/textfield';
import { useTracker } from '@lichtblick/tracker';

import { submitForm } from './actions';
import { Checkbox } from './Checkbox';
import { Confirmation } from './Confirmation';
import { ErrorContext } from './ErrorBoundary';
import { COMMODITIES } from './Form.content';
import { debounce, getFormError, getFormProduct } from './Form.helpers';
import styles from './Form.module.scss';
import { LeadFormValues } from './Form.types';
import { OptionalSubsection } from './OptionalSubsection';
import { Textarea } from './Textarea';
import { Textfield } from './Textfield';

type FormProps = {
  commodity: keyof typeof COMMODITIES;
};

export const Form: FC<FormProps> = ({ commodity }) => {
  const formRef = useRef<HTMLFormElement>(null);
  const { trackCustomEvent } = useTracker();
  const { outerError } = useContext(ErrorContext); // Note: Form data is lost on outer error, but that's fine for now
  const [state, dispatch, pending] = useActionState(submitForm, {});
  const { error, input, submitted } = state;
  const errorMessage = outerError ? 'Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.' : error;
  const { fields, formName, headline, option } = COMMODITIES[commodity];

  const trackFocus = useCallback(
    () => trackCustomEvent({ eventName: 'form_open', customProps: { form_name: formName } }),
    [formName, trackCustomEvent],
  );

  const handleFieldError = useCallback(() => {
    const invalidFields = formRef.current?.querySelectorAll<HTMLElement>(':is(input, select, textarea):invalid');

    invalidFields?.[0]?.focus();
    trackCustomEvent({
      eventName: 'form_failed',
      customProps: { form_name: formName, error_message: getFormError(invalidFields) },
    });
  }, [formName, trackCustomEvent]);

  const handleFailedSubmit = useCallback(() => debounce(handleFieldError), [handleFieldError]);

  useEffect(() => {
    if (submitted) {
      trackCustomEvent({
        eventName: `form_submit_B2B_${getFormProduct(input)}`,
        customProps: { unique_id: undefined },
      });
    } else if (errorMessage) {
      trackCustomEvent({ eventName: 'form_failed', customProps: { form_name: formName, error_message: errorMessage } });
    }
  }, [errorMessage, formName, input, submitted, trackCustomEvent]);

  if (submitted) {
    return <Confirmation email={input.emailAddress} onBackButtonClick={() => startTransition(dispatch)} />;
  }

  const getProps = (
    name: keyof LeadFormValues,
    optional = false,
  ): Pick<TextfieldProps, 'defaultValue' | 'name' | 'required' | 'subLabel'> => ({
    defaultValue: input?.[name]?.toString(),
    name,
    required: !optional,
    subLabel: optional ? 'optional' : undefined,
  });

  return (
    <section>
      <Headline className={styles.headline} renderAs="h2" size="m">
        {headline}
      </Headline>
      <Headline className={styles['sub-headline']} renderAs="h3" size="s">
        Einfach Formular ausfüllen und ein*e LichtBlick-Berater*in meldet sich.
      </Headline>
      <form action={dispatch} onFocus={trackFocus} onInvalid={handleFailedSubmit} ref={formRef}>
        <fieldset className={styles.section}>
          <Textfield {...getProps('companyName')} className={styles.field} label="Unternehmen" />
        </fieldset>
        <fieldset className={styles.section}>
          <Textfield {...getProps('street')} className={styles.field} label="Straße und Hausnummer" />
          <Textfield
            {...getProps('postalCode')}
            className={styles.field}
            label="Postleitzahl"
            messages={{ pattern: 'Ungültige Postleitzahl' }}
            pattern="^\d{5}$"
          />
          <Textfield {...getProps('city')} className={styles.field} label="Ort" />
        </fieldset>
        <fieldset className={styles.section}>
          <Textfield {...getProps('firstname')} className={styles.field} label="Vorname" />
          <Textfield {...getProps('lastname')} className={styles.field} label="Nachname" />
          <Textfield
            {...getProps('emailAddress')}
            className={styles.field}
            label="E-Mail-Adresse"
            messages={{ invalid: 'Ungültige E-Mail-Adresse' }}
            type="email"
          />
          <Textfield
            {...getProps('telephoneNumber')}
            className={styles.field}
            label="Telefonnummer"
            messages={{ pattern: 'Ungültige Telefonnummer' }}
            pattern="^\(?(\+\d|0)([ \(\)\/\-]*\d){5,}$"
            type="tel"
          />
        </fieldset>
        <fieldset className={styles.section}>
          <Headline renderAs="h4" size="s">
            Ihr Verbrauch
          </Headline>
          <Textfield
            {...getProps(fields.consumption)}
            className={styles.field}
            label="Jahresplanverbrauch"
            min="0"
            suffixText="kWh"
            type="number"
          />
          <Textfield
            {...getProps(fields.meters, true)}
            className={styles.field}
            label="Anzahl Zähler"
            min="0"
            type="number"
          />
        </fieldset>
        <fieldset className={styles.section}>
          <Headline renderAs="h4" size="s">
            {option.headline}
          </Headline>
          <OptionalSubsection
            icon={option.icon}
            isOpen={Boolean(input?.[option.fields.consumption])}
            label={option.label}
          >
            <div className={styles.subsection}>
              <Textfield
                {...getProps(option.fields.consumption)}
                className={styles.field}
                label="Jahresplanverbrauch"
                min="0"
                suffixText="kWh"
                type="number"
              />
              <Textfield
                {...getProps(option.fields.meters, true)}
                className={styles.field}
                label="Anzahl Zähler"
                min="0"
                type="number"
              />
            </div>
          </OptionalSubsection>
          <Textarea
            {...getProps('customerMessage', true)}
            className={classNames(styles.field, styles.spaced)}
            label="Nachricht"
            rows={4}
          />
          <Checkbox
            labelSlot={
              <span>
                Ja, ich willige ein, dass mich LichtBlick sowie mit LichtBlick verbundene Unter&shy;nehmen über Strom-,
                FahrStrom und Gas-Produkte sowie über energienahe Produkte und Dienstleistungen (z.B. Smartmeter,
                Wallboxen, alternative Energie&shy;anlagen oder Energieberatung), Bonusaktionen (z.B. „Kunden werben
                Kunden“), sowie zu Marktforschungszwecken und zur Qualitätssicherung telefonisch oder per E-Mail
                kontaktieren und informieren. Ich habe die{' '}
                <TextCTA as="a" href="/datenschutz/" inlineLink target="_blank">
                  Datenschutzhinweise
                </TextCTA>{' '}
                zur Kenntnis genommen und bin darüber informiert, dass die vorstehende, freiwillige Einwilligung
                jederzeit ohne Angabe von Gründen mit Wirkung für die Zukunft (per Post an LichtBlick&nbsp;SE,
                Klostertor&nbsp;1, 20097&nbsp;Hamburg, per E-Mail an{' '}
                <TextCTA as="a" href="mailto:datenschutz@lichtblick.de" inlineLink>
                  datenschutz@lichtblick.de
                </TextCTA>{' '}
                oder per Telefon unter{' '}
                <TextCTA as="a" href="tel:+494080803034" inlineLink>
                  040 80803034
                </TextCTA>
                ) widerrufen werden kann.
              </span>
            }
            name="marketingOptIn"
            required
          />
        </fieldset>
        <BoxCTA as="button" className={styles.cta} loading={pending} type="submit" variant="primary">
          Jetzt abschicken
        </BoxCTA>
        {errorMessage && !pending && (
          <FieldFooter className={styles['form-error']} icon={AlertIconXs} label={errorMessage} state="error" />
        )}
      </form>
    </section>
  );
};
